'use client';

import * as HoverCardPrimitive from '@radix-ui/react-hover-card';
import * as React from 'react';

import { cn } from '../../lib/utils';

import {
  HoverSurfaceInteractionContext,
  useHoverSurfaceClosePolicy,
  useHoverSurfaceTriggerHandlers,
} from './hover-surface-close-policy';

/**
 * Holds the card open through a click on its own trigger (HAR-4016): clicking
 * the device chip should not take away the "Device Details" you hovered to
 * read. The policy — and the reasoning behind its narrow scope — lives in
 * `hover-surface-close-policy.tsx`, shared with `Tooltip`.
 *
 * `HoverCardTrigger` has no pointer-down/click handlers of its own, so unlike
 * Tooltip the only route here is the portalled content's `DismissableLayer`
 * counting a click on the trigger as "outside". The shared policy covers it
 * because every route reports through `onOpenChange`.
 */
const HoverCard = ({
  open,
  defaultOpen,
  onOpenChange,
  children,
  ...props
}: React.ComponentPropsWithoutRef<typeof HoverCardPrimitive.Root>) => {
  const policy = useHoverSurfaceClosePolicy({
    open,
    defaultOpen,
    onOpenChange,
  });

  return (
    <HoverSurfaceInteractionContext.Provider value={policy.interaction}>
      <HoverCardPrimitive.Root
        {...props}
        open={policy.open}
        onOpenChange={policy.onOpenChange}
      >
        {children}
      </HoverCardPrimitive.Root>
    </HoverSurfaceInteractionContext.Provider>
  );
};
HoverCard.displayName = 'HoverCard';

const HoverCardTrigger = React.forwardRef<
  React.ElementRef<typeof HoverCardPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof HoverCardPrimitive.Trigger>
>(({ onPointerDown, onClick, ...props }, ref) => {
  const handlers = useHoverSurfaceTriggerHandlers({ onPointerDown, onClick });

  // Handlers after the spread so a caller's props cannot replace them.
  return <HoverCardPrimitive.Trigger ref={ref} {...props} {...handlers} />;
});
HoverCardTrigger.displayName = HoverCardPrimitive.Trigger.displayName;

const HoverCardContent = React.forwardRef<
  React.ElementRef<typeof HoverCardPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof HoverCardPrimitive.Content>
>(({ className, align = 'center', sideOffset = 4, ...props }, ref) => (
  // Portal so the card escapes any clipping ancestor (e.g. a table cell with
  // overflow: hidden), matching the canonical shadcn hover-card and Tooltip.
  <HoverCardPrimitive.Portal>
    <HoverCardPrimitive.Content
      ref={ref}
      align={align}
      sideOffset={sideOffset}
      className={cn(
        'z-50 w-72 rounded-md border bg-popover p-4 text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-[--radix-hover-card-content-transform-origin] duration-150 ease-out-strong',
        className,
      )}
      {...props}
    />
  </HoverCardPrimitive.Portal>
));
HoverCardContent.displayName = HoverCardPrimitive.Content.displayName;

export { HoverCard, HoverCardTrigger, HoverCardContent };
